import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Skeleton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip,
} from '@mui/material';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { setSelectedCoin } from '../../store/Slices/spotTradingSlice';

const CoinDetails = ({ spotAssets }) => {
  const dispatch = useDispatch();
  const { selectedCoin } = useSelector((state) => state.spotTrading);
  const themeMode = useSelector((state) => (state.theme.light ? 'light' : 'dark'));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [coinData, setCoinData] = useState(null);
  const [livePrice, setLivePrice] = useState(null);
  const [liveChange, setLiveChange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [coinDrawerOpen, setCoinDrawerOpen] = useState(false);
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);
  const [chartDrawerOpen, setChartDrawerOpen] = useState(false);
  const [ws, setWs] = useState(null);

  useEffect(() => {
    const fetchCoinData = async () => {
      if (!selectedCoin) return;

      setLoading(true);
      setError(null);

      try {
        const symbol = selectedCoin.toUpperCase();
        const response = await axios.get('https://api.binance.com/api/v3/ticker/24hr', {
          params: { symbol },
          timeout: 10000,
        });

        const data = response.data;

        if (!data.lastPrice || !data.priceChangePercent) {
          throw new Error('Invalid data format from Binance API');
        }

        const coinDetails = {
          symbol: data.symbol.replace('USDT', ''),
          price: parseFloat(data.lastPrice),
          change: parseFloat(data.priceChangePercent),
          volume24h: parseFloat(data.volume),
          high24h: parseFloat(data.highPrice),
          low24h: parseFloat(data.lowPrice),
        };

        setCoinData(coinDetails);
        setLivePrice(coinDetails.price);
        setLiveChange(coinDetails.change);
      } catch (err) {
        console.error('Error fetching coin data:', err);
        setError(`Failed to fetch data for ${selectedCoin}. Please try again.`);
        setCoinData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCoinData();
  }, [selectedCoin]);

  useEffect(() => {
    if (!selectedCoin) return;

    if (ws) {
      ws.close();
    }

    const symbol = selectedCoin.toLowerCase();
    const newWs = new WebSocket(`wss://stream.binance.com:9443/ws/${symbol}@ticker`);

    newWs.onopen = () => {
      console.log('WebSocket connected for:', symbol);
    };

    newWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data && data.c && data.P) {
          setLivePrice(parseFloat(data.c));
          setLiveChange(parseFloat(data.P));
        }
      } catch (err) {
        console.error('Error processing WebSocket message:', err);
      }
    };

    newWs.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('Live price update disconnected');
    };

    newWs.onclose = () => {
      console.log('WebSocket closed for:', symbol);
    };

    setWs(newWs);

    return () => {
      newWs.close();
    };
  }, [selectedCoin]);

  const toggleCoinDrawer = (open) => (event) => {
    if (event?.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setCoinDrawerOpen(open);
  };

  const toggleInfoDrawer = (open) => (event) => {
    if (event?.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setInfoDrawerOpen(open);
  };

  const toggleChartDrawer = (open) => (event) => {
    if (event?.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setChartDrawerOpen(open);
  };

  const handleCoinSelect = (coinSymbol) => {
    dispatch(setSelectedCoin(coinSymbol));
    setCoinDrawerOpen(false);
  };

  // لون السعر / النسبة حسب الزيادة أو النقصان
  const priceColor = liveChange >= 0 ? theme.palette.success.main : theme.palette.error.main;
  const changeSign = liveChange > 0 ? '+' : '';

  // حالة عدم اختيار عملة بعد
  if (!selectedCoin) {
    return (
      <Box
        sx={{
          p: 2,
          cursor: 'pointer',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          border: `1px dashed ${
            theme.palette.mode === 'dark'
              ? 'rgba(148,163,184,0.6)'
              : 'rgba(148,163,184,0.8)'
          }`,
          color: theme.palette.text.secondary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          '&:hover': {
            borderColor: theme.palette.brand.main,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(243,186,47,0.04)'
                : 'rgba(243,186,47,0.06)',
            transition: 'all 0.2s ease',
          },
        }}
        onClick={() => setCoinDrawerOpen(true)}
      >
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Select a coin to start trading
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            Tap here to open the market list
          </Typography>
        </Box>
        <ExpandMoreIcon sx={{ color: theme.palette.brand.main }} />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          p: 2,
          flexWrap: 'wrap',
          maxWidth: '100%',
          bgcolor: theme.palette.background.paper,
          borderRadius: 2.5,
        }}
      >
        <Skeleton variant="rounded" width={140} height={28} />
        <Skeleton variant="text" width={120} height={32} />
        <Skeleton variant="text" width={120} height={32} />
        <Skeleton variant="rounded" width={180} height={26} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 2,
          maxWidth: '100%',
          bgcolor: theme.palette.background.paper,
          borderRadius: 2.5,
        }}
      >
        <Typography sx={{ color: theme.palette.error.main }}>{error}</Typography>
      </Box>
    );
  }

  const symbolText = coinData?.symbol || selectedCoin.replace('USDT', '');

  return (
    <Box sx={{ maxWidth: '100%' }}>
      {/* Coin Selection Drawer */}
      <Drawer anchor="left" open={coinDrawerOpen} onClose={toggleCoinDrawer(false)}>
        <Box
          sx={{
            width: 280,
            p: 2,
            backgroundColor: theme.palette.background.paper,
            borderTopRightRadius: 16,
            borderBottomRightRadius: 16,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
              }}
            >
              Select Market
            </Typography>
            <IconButton size="small" onClick={toggleCoinDrawer(false)}>
              <CloseIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
            </IconButton>
          </Box>

          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, mb: 1.5 }}
          >
            Spot · USDT pairs
          </Typography>

          <Divider sx={{ mb: 1.5, bgcolor: theme.palette.divider }} />

          {spotAssets?.length === 0 ? (
            <Typography
              sx={{
                textAlign: 'center',
                color: theme.palette.text.secondary,
                mt: 4,
              }}
            >
              No coins available
            </Typography>
          ) : (
            <List
              sx={{
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 150px)',
              }}
            >
              {spotAssets?.map((asset, index) => (
                <ListItem
                  key={`${asset.symbol}-${index}`}
                  onClick={() => handleCoinSelect(`${asset.symbol}USDT`)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 1.5,
                    mb: 0.5,
                    px: 1.5,
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(148,163,184,0.18)'
                          : 'rgba(15,23,42,0.04)',
                      transition: 'background-color 0.18s ease',
                    },
                  }}
                >
                  <ListItemText
                    primary={`${asset.symbol} / USDT`}
                    secondary={asset.name}
                    primaryTypographyProps={{
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      fontSize: 14,
                    }}
                    secondaryTypographyProps={{
                      color: theme.palette.text.secondary,
                      fontSize: 12,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Main Coin Info Card */}
      <Box
        sx={{
          p: { xs: 1.5, md: 2 },
          borderRadius: 2.5,
          backgroundColor: theme.palette.background.paper,
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 16px 40px rgba(0,0,0,0.65)'
              : '0 12px 30px rgba(15,23,42,0.16)',
          border: `1px solid ${
            theme.palette.mode === 'dark'
              ? 'rgba(148,163,184,0.28)'
              : 'rgba(15,23,42,0.06)'
          }`,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, md: 3 },
        }}
      >
        {/* LEFT: Symbol + price */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
            }}
            onClick={() => setCoinDrawerOpen(true)}
          >
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                letterSpacing: 0.4,
              }}
            >
              {symbolText}/USDT
            </Typography>

            <Chip
              label="Spot"
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                borderRadius: 10,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(243,186,47,0.18)'
                    : 'rgba(243,186,47,0.16)',
                color: theme.palette.brand.main,
                fontWeight: 500,
              }}
            />

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setCoinDrawerOpen(true);
              }}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.brand.main,
                  transition: 'color 0.2s ease',
                },
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection:'row',
              gap: 1.25,
            }}
          >
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              sx={{
                color: priceColor,
                fontWeight: 500,
              }}
            >
              {livePrice || '--'}
            </Typography>

            <Chip
              label={
                liveChange != null
                  ? `${changeSign}${liveChange.toFixed(2)}%`
                  : '--%'
              }
              size="small"
              sx={{
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 500,
                bgcolor:
                  liveChange >= 0
                    ? 'rgba(22,163,74,0.18)'
                    : 'rgba(220,38,38,0.16)',
                color:
                  liveChange >= 0
                    ? theme.palette.success.main
                    : theme.palette.error.main,
              }}
            />
          </Box>

          {/* {!isMobile && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                mt: 0.3,
              }}
            >
              Live price · data from Binance
            </Typography>
          )} */}
        </Box>

        {/* RIGHT: 24H stats + mobile actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-end', md: 'center' },
            justifyContent: { xs: 'space-between', md: 'flex-end' },
            width: '100%',
            gap: { xs: 1.5, md: 3 },
          }}
        >
          {/* Desktop 24H stats */}
          {!isMobile && coinData && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  24H High
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                >
                  {coinData.high24h?.toFixed(4)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  24H Low
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                >
                  {coinData.low24h?.toFixed(4)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  24H Volume
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                >
                  {(coinData.volume24h / 1000).toFixed(2)}K
                </Typography>
              </Box>
            </Box>
          )}

          {/* Mobile: Info + Chart buttons */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Market details">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleInfoDrawer(true)(e);
                  }}
                  sx={{
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: theme.palette.brand.main,
                      transition: 'color 0.2s ease',
                    },
                  }}
                >
                  <InfoIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open chart">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleChartDrawer(true)(e);
                  }}
                  sx={{
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: theme.palette.brand.main,
                      transition: 'color 0.2s ease',
                    },
                  }}
                >
                  <ShowChartIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      {/* Info Drawer (Market Details – Mobile) */}
      {isMobile && (
        <Drawer anchor="top" open={infoDrawerOpen} onClose={toggleInfoDrawer(false)}>
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.palette.background.paper,
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                  : '0 4px 12px rgba(0, 0, 0, 0.1)',
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
              >
                Market Details
              </Typography>
              <IconButton onClick={toggleInfoDrawer(false)}>
                <CloseIcon sx={{ color: theme.palette.text.secondary }} />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2, bgcolor: theme.palette.divider }} />

            {coinData && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  24H Vol: {(coinData.volume24h / 1000).toFixed(2)}K
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  24H High: {coinData.high24h?.toFixed(4)}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  24H Low: {coinData.low24h?.toFixed(4)}
                </Typography>
              </Box>
            )}
          </Box>
        </Drawer>
      )}

      {/* Chart Drawer (Full Screen – Mobile) */}
      {isMobile && (
        <Drawer anchor="top" open={chartDrawerOpen} onClose={toggleChartDrawer(false)}>
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.palette.background.paper,
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                  : '0 4px 12px rgba(0, 0, 0, 0.1)',
              width: '100%',
              height: '100vh',
              maxWidth: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
              >
                {symbolText}/USDT Chart
              </Typography>
              <IconButton onClick={toggleChartDrawer(false)}>
                <CloseIcon sx={{ color: theme.palette.text.secondary }} />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2, bgcolor: theme.palette.divider }} />

            <Box sx={{ height: 'calc(100% - 80px)', width: '100%' }}>
              <iframe
                src={`https://s.tradingview.com/widgetembed/?symbol=BINANCE:${selectedCoin}&interval=1H&theme=${themeMode}&style=1&toolbarbg=f1f3f6`}
                style={{ width: '100%', height: '100%', border: 'none', borderRadius: '8px' }}
                title="TradingView Chart"
              />
            </Box>
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default CoinDetails;
